import { useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';
import { changePasswordRequest } from '@/services/auth';

/**
 * Plan 06-03 ChangePasswordPage at `/settings/change-password`.
 *
 * Voluntary password change. Reachable from UserMenu → "Change password". On success
 * we keep the user signed in (their existing access token does NOT carry
 * `force_password_change=true`, so future requests continue to authorise) and route
 * back to `/dashboard`.
 */

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'New password must be at least 8 characters')
      .regex(/[a-z]/, 'Must contain a lowercase letter')
      .regex(/[A-Z]/, 'Must contain an uppercase letter')
      .regex(/\d/, 'Must contain a digit')
      .regex(/[^a-zA-Z0-9]/, 'Must contain a non-alphanumeric character'),
    confirmPassword: z.string().min(1, 'Please confirm the new password'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'New password and confirmation do not match',
  });

type PasswordFormValues = z.input<typeof passwordSchema>;

export function ChangePasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const { markPasswordChanged } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useZodForm({
    schema: passwordSchema,
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  async function onSubmit(values: PasswordFormValues): Promise<void> {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await changePasswordRequest({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      // REVIEW FF-018: flip the in-memory flag so a user who landed here from
      // a force-change-required state isn't bounced back by
      // ForcePasswordChangeGuard.
      markPasswordChanged();
      toast.success('Password changed.');
      void navigate('/dashboard', { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorMessage(e.detail ?? 'Could not change password.');
        toast.error('Could not change password', { description: e.detail ?? e.message });
      } else {
        setErrorMessage('Network error — please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6 max-w-lg">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Change Password</h1>
        <p className="text-muted-foreground">Use the form below to change your account password.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Update password</CardTitle>
          <CardDescription>
            New passwords must be at least 8 characters and include lower-case, upper-case, digit,
            and non-alphanumeric characters.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorMessage !== null && (
            <div
              role="alert"
              data-testid="change-password-error"
              className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
            >
              {errorMessage}
            </div>
          )}
          <form
            onSubmit={(e) => {
              void form.handleSubmit(onSubmit)(e);
            }}
            className="space-y-4"
            noValidate
          >
            <div className="space-y-2">
              <Label htmlFor="cp-current">Current password</Label>
              <Input
                id="cp-current"
                type="password"
                autoComplete="current-password"
                disabled={submitting}
                aria-invalid={form.formState.errors.currentPassword !== undefined}
                {...form.register('currentPassword')}
                data-testid="change-password-current"
              />
              {form.formState.errors.currentPassword && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.currentPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cp-new">New password</Label>
              <Input
                id="cp-new"
                type="password"
                autoComplete="new-password"
                disabled={submitting}
                aria-invalid={form.formState.errors.newPassword !== undefined}
                {...form.register('newPassword')}
                data-testid="change-password-new"
              />
              {form.formState.errors.newPassword && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.newPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cp-confirm">Confirm new password</Label>
              <Input
                id="cp-confirm"
                type="password"
                autoComplete="new-password"
                disabled={submitting}
                aria-invalid={form.formState.errors.confirmPassword !== undefined}
                {...form.register('confirmPassword')}
                data-testid="change-password-confirm"
              />
              {form.formState.errors.confirmPassword && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.confirmPassword.message}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={submitting}
                onClick={() => {
                  void navigate('/dashboard');
                }}
                data-testid="change-password-cancel"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="change-password-submit">
                {submitting ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
